import { useLocation, useParams } from 'react-router-dom';
import { listAs2DlqTasks } from '../api/as2Dlq';
import { getAs2OutboundMessage } from '../api/as2OutboundMessages';
import { CopyButton } from '../components/CopyButton';
import { PageHeader } from '../components/FeatureStatus';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { RelatedLinks } from '../components/RelatedLinks';
import { Timeline } from '../components/Timeline';
import { useAsyncData } from '../hooks/useAsyncData';
import { findMatchingAs2DlqTask } from '../lib/as2DlqMatch';
import { as2DlqTasksLink, documentsByCorrelationLink, exchangesByCorrelationLink, executionsByCorrelationLink } from '../lib/crossLinks';
import { mergeTimelineEvents } from '../lib/timeline';
import type { As2CertificateSummary, As2DeliveryAttempt } from '../types/api';

// Bounds the client-side DLQ lookup below to the first page of OUTBOUND_SEND tasks for this
// message's partner — enough to find a match in the normal case without an unbounded fetch.
// See lib/as2DlqMatch for why matching by referenceId is exact rather than a heuristic.
const DLQ_LOOKUP_SIZE = 200;

// Detail view for a single AS2 gateway outbound message record (transport-level: signing,
// encryption, MDN, retry/delivery-attempt state). There is no payload/body field on the
// backing OutboundMessageResponse DTO, so this page intentionally has no Payload tab.

export function As2OutboundMessageDetailPage() {
  const params = useParams<{ id: string }>();
  const { search } = useLocation();
  const messageId = params.id ?? '';
  const { data, isLoading, error, refresh } = useAsyncData(() => getAs2OutboundMessage(messageId), [messageId]);
  const { data: dlqTasks } = useAsyncData(
    () => (data
      ? listAs2DlqTasks({ taskType: 'OUTBOUND_SEND', partnerCode: data.partnerCode, size: DLQ_LOOKUP_SIZE })
      : Promise.resolve({ data: [], total: 0, page: 0, pageSize: 0 })),
    [data?.messageId, data?.partnerCode],
  );

  if (isLoading) return <LoadingState label="Loading AS2 outbound message..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!data) return null;

  const matchingDlqTask = findMatchingAs2DlqTask(dlqTasks?.data ?? [], data.messageId);

  const timeline = mergeTimelineEvents([
    { id: 'sent', label: 'Sent', at: data.sentAt, tone: 'neutral' },
    { id: 'async-mdn-received', label: 'Async MDN received', at: data.asyncMdnReceivedAt, tone: 'success' },
    { id: 'mdn-recorded', label: 'MDN recorded', at: data.mdn?.receivedAt, tone: 'success' },
    { id: 'last-retry', label: 'Last retry attempt', at: data.retry?.lastRetryAt, tone: 'warning' },
    { id: 'next-retry', label: 'Next retry scheduled', at: data.retry?.nextRetryAt, tone: 'warning' },
    { id: 'dead-lettered', label: 'Dead-lettered', at: data.retry?.deadLetteredAt, tone: 'error' },
  ]);

  return (
    <div className="page-grid">
      <PageHeader
        eyebrow="AS2 Gateway"
        title="AS2 outbound message"
        purpose="Transport-level detail for a single outbound AS2 send: signing, encryption, MDN state, and retry/delivery history."
        status="PRODUCTION_READY"
        connected
        next="No payload is stored on this transport record. Cross-reference the correlation ID against the business-level Messages log if you need the document content."
      />

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>{data.as2MessageId ?? data.messageId}</h2>
            <span className="inline-copy">
              <span className="mono" title={data.messageId}>{data.messageId}</span>
              <CopyButton value={data.messageId} label="Copy message ID" />
            </span>
          </div>
          <StatusBadge value={data.status} />
        </div>
        <div className="summary-grid">
          <div><strong>Partner code</strong><span>{data.partnerCode ?? 'n/a'}</span></div>
          <div><strong>Document type</strong><span>{data.documentType ?? 'n/a'}</span></div>
          <div><strong>Content type</strong><span>{data.contentType ?? 'n/a'}</span></div>
          <div>
            <strong>Correlation ID</strong>
            <span className="inline-copy">
              <span className="mono">{data.correlationId ?? 'n/a'}</span>
              {data.correlationId ? (
                <>
                  <CopyButton value={data.correlationId} label="Copy correlation ID" />
                  <RelatedLinks links={[
                    documentsByCorrelationLink(data.correlationId, search),
                    exchangesByCorrelationLink(data.correlationId, search),
                    executionsByCorrelationLink(data.correlationId, search),
                  ]}
                  />
                </>
              ) : null}
            </span>
          </div>
          <div><strong>Sent at</strong><span>{data.sentAt ? new Date(data.sentAt).toLocaleString('en-GB') : 'n/a'}</span></div>
          <div><strong>Failure reason</strong><span>{data.failureReason ?? 'n/a'}</span></div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Protocol &amp; security</h2>
            <p>Signing, encryption, and MIC state recorded for this send attempt.</p>
          </div>
        </div>
        <div className="summary-grid">
          <div><strong>MIC value</strong><span className="mono">{data.micValue ?? 'n/a'}</span></div>
          <div><strong>MIC algorithm</strong><span>{data.micAlgorithm ?? 'n/a'}</span></div>
          <div><strong>Security processing status</strong><span>{data.securityProcessingStatus ?? 'n/a'}</span></div>
          <div><strong>MDN mode</strong><span>{data.mdnMode ?? 'n/a'}</span></div>
          <div><strong>Waiting for async MDN</strong><span>{data.waitingForAsyncMdn ? 'Yes' : 'No'}</span></div>
          <div><strong>Async MDN receive URL</strong><span className="mono">{data.asyncMdnReceiveUrl ?? 'n/a'}</span></div>
        </div>
        <div className="page-grid two-columns">
          <div className="panel-subsection">
            <h3>Signing</h3>
            <div className="stack-list">
              <div className="stack-item"><strong>Signed</strong><span>{data.signed ? 'Yes' : 'No'}</span></div>
              <div className="stack-item"><strong>Signing algorithm</strong><span>{data.signingAlgorithm ?? 'n/a'}</span></div>
              {renderCertificate(data.signingCertificate)}
            </div>
          </div>
          <div className="panel-subsection">
            <h3>Encryption</h3>
            <div className="stack-list">
              <div className="stack-item"><strong>Encrypted</strong><span>{data.encrypted ? 'Yes' : 'No'}</span></div>
              <div className="stack-item"><strong>Encryption algorithm</strong><span>{data.encryptionAlgorithm ?? 'n/a'}</span></div>
              {renderCertificate(data.encryptionCertificate)}
            </div>
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>MDN</h2>
            <p>Message disposition notification received for this send attempt, if any.</p>
          </div>
        </div>
        {data.mdn ? (
          <div className="summary-grid">
            <div><strong>Mode</strong><span>{data.mdn.mode ?? 'n/a'}</span></div>
            <div><strong>Disposition</strong><span>{data.mdn.disposition ?? 'n/a'}</span></div>
            <div><strong>Received content MIC</strong><span className="mono">{data.mdn.receivedContentMic ?? 'n/a'}</span></div>
            <div><strong>Signature status</strong><span>{data.mdn.signatureStatus ?? 'n/a'}</span></div>
            <div><strong>Signature valid</strong><span>{data.mdn.signatureValid === undefined ? 'n/a' : data.mdn.signatureValid ? 'Yes' : 'No'}</span></div>
            <div><strong>Validation status</strong><span>{data.mdn.validationStatus ?? 'n/a'}</span></div>
            <div><strong>Failure reason</strong><span>{data.mdn.failureReason ?? 'n/a'}</span></div>
            <div><strong>Received at</strong><span>{data.mdn.receivedAt ? new Date(data.mdn.receivedAt).toLocaleString('en-GB') : 'n/a'}</span></div>
            {data.mdn.signingCertificate ? (
              <div className="full-width">
                <strong>Signing certificate</strong>
                <span>{formatCertificate(data.mdn.signingCertificate)}</span>
              </div>
            ) : null}
          </div>
        ) : <EmptyState label="No MDN has been recorded for this message yet." />}
        {data.mdn?.rawBody ? (
          <div className="panel-subsection">
            <div className="panel-header">
              <h3>Raw MDN</h3>
              <CopyButton value={data.mdn.rawBody} label="Copy raw MDN" />
            </div>
            <p>Disposition report body after MIME/multipart-signed envelope stripping -- not the original wire bytes.</p>
            <pre className="code-block content-viewer raw-payload-viewer">{data.mdn.rawBody}</pre>
          </div>
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Retry &amp; delivery attempts</h2>
            <p>Retry scheduling state and the individual HTTP delivery attempts recorded for this message.</p>
          </div>
          {matchingDlqTask ? (
            <RelatedLinks links={[as2DlqTasksLink('OUTBOUND_SEND', data.partnerCode, search)]} />
          ) : null}
        </div>
        {data.retry ? (
          <div className="summary-grid">
            <div><strong>Retry state</strong><span>{data.retry.retryState ?? 'n/a'}</span></div>
            <div><strong>Retry count</strong><span>{data.retry.retryCount ?? 0}/{data.retry.maxAttempts ?? 'n/a'}</span></div>
            <div><strong>Retry category</strong><span>{data.retry.retryCategory ?? 'n/a'}</span></div>
            <div><strong>Next retry at</strong><span>{data.retry.nextRetryAt ? new Date(data.retry.nextRetryAt).toLocaleString('en-GB') : 'n/a'}</span></div>
            <div><strong>Last retry at</strong><span>{data.retry.lastRetryAt ? new Date(data.retry.lastRetryAt).toLocaleString('en-GB') : 'n/a'}</span></div>
            <div><strong>Last retry reason</strong><span>{data.retry.lastRetryReason ?? 'n/a'}</span></div>
            <div><strong>Dead-lettered at</strong><span>{data.retry.deadLetteredAt ? new Date(data.retry.deadLetteredAt).toLocaleString('en-GB') : 'n/a'}</span></div>
            <div><strong>Dead-letter reason</strong><span>{data.retry.deadLetterReason ?? 'n/a'}</span></div>
          </div>
        ) : <p>No retry activity recorded for this message.</p>}
        {data.deliveryAttempts.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Status</th>
                <th>HTTP status</th>
                <th>MDN</th>
                <th>Started</th>
                <th>Completed</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {data.deliveryAttempts.map((attempt) => renderAttemptRow(attempt))}
            </tbody>
          </table>
        ) : <p>No delivery attempts recorded for this message.</p>}
      </section>

      {timeline.length > 0 ? (
        <section className="panel">
          <div className="panel-header">
            <h2>Timeline</h2>
          </div>
          <Timeline events={timeline} />
        </section>
      ) : null}
    </div>
  );
}

function renderAttemptRow(attempt: As2DeliveryAttempt) {
  return (
    <tr key={attempt.attemptNumber}>
      <td>{attempt.attemptNumber}</td>
      <td><StatusBadge value={attempt.status} /></td>
      <td>{attempt.httpStatus ?? 'n/a'}</td>
      <td>{attempt.mdnDetected ? `${attempt.mdnDisposition ?? 'detected'} (${attempt.mdnSignatureValid ? 'valid' : attempt.mdnSignatureStatus ?? 'unverified'})` : 'n/a'}</td>
      <td>{attempt.startedAt ? new Date(attempt.startedAt).toLocaleString('en-GB') : 'n/a'}</td>
      <td>{attempt.completedAt ? new Date(attempt.completedAt).toLocaleString('en-GB') : 'n/a'}</td>
      <td>{attempt.errorMessage ?? 'n/a'}</td>
    </tr>
  );
}

function renderCertificate(certificate?: As2CertificateSummary) {
  if (!certificate) {
    return <div className="stack-item"><strong>Certificate</strong><span>n/a</span></div>;
  }
  return (
    <div className="stack-item">
      <strong>Certificate</strong>
      <span>{formatCertificate(certificate)}</span>
    </div>
  );
}

function formatCertificate(certificate: As2CertificateSummary): string {
  const parts = [certificate.alias, certificate.fingerprintSha256, certificate.validUntil ? `valid until ${new Date(certificate.validUntil).toLocaleDateString('en-GB')}` : undefined];
  return parts.filter(Boolean).join(' - ') || certificate.id;
}
